import * as React from 'react';

import classNames from 'classnames';

import {Button} from '../button/button';
import {SaveButton} from '../button/save-button/save-button';
import type {ButtonSize} from '../button/types';
import {ButtonGroup} from '../button-group/button-group';
import {Header} from '../typography/header';
import {Subheader} from '../typography/subheader';

import {ErrorMessage} from './error-message';

import styles from './form-wrapper-with-buttons.module.css';

type Props = {
    title: string;
    subtitle?: string;
    errorMessage?: string;
    onSaveClick: (e: React.ChangeEvent<any>) => void;
    onCancel: (e?: React.SyntheticEvent<any>) => void;
    isSaving: boolean;
    buttonText?: string;
    savingText?: string;
    successText?: string;
    saveButtonDisabled?: boolean;
    onClearSaveComplete?: () => any;
    errorSaving?: boolean;
    resetOnSaveComplete?: boolean;
    children: React.ReactNode;
    hideTitle?: boolean;
    flexDirection?: 'row' | 'column';
    noPadding?: boolean;
    buttonSize?: ButtonSize;
    disabledTooltip?: string;
};

export function FormWrapperWithButtons(props: Props) {
    const {flexDirection = 'column', buttonSize = 'big'} = props;

    const wrapperClassName = classNames(
        'flex',
        'align-center',
        'justify-center',
        'no-padding',
        'full-width',
        {
            ['flex-dir-row']: flexDirection === 'row',
            ['flex-dir-col']: flexDirection === 'column',
            ['py-8']: !props.noPadding,
        }
    );

    const buttonsClassName = classNames('flex', {
        ['mx-8']: flexDirection === 'row',
        ['mt-24 align-self-end']: flexDirection === 'column',
    });

    return (
        <section className={wrapperClassName}>
            {!props.hideTitle && (
                <div className={classNames('flex flex-dir-col gap-8', styles.labels)}>
                    <Header>{props.title}</Header>
                    <Subheader>{props.subtitle}</Subheader>
                    {props.errorMessage ? (
                        <ErrorMessage>{props.errorMessage}</ErrorMessage>
                    ) : undefined}
                </div>
            )}
            {props.children}
            <div className={buttonsClassName}>
                <ButtonGroup>
                    <Button size={buttonSize} onClick={props.onCancel}>
                        Cancel
                    </Button>
                    <SaveButton
                        size={buttonSize}
                        isSaving={props.isSaving}
                        onClearSaveComplete={
                            props.onClearSaveComplete ? props.onClearSaveComplete : undefined
                        }
                        savingText={props.savingText}
                        successText={props.successText}
                        // @ts-expect-error
                        onClick={props.onSaveClick}
                        disabled={props.saveButtonDisabled}
                        disabledTooltip={props.disabledTooltip}
                        errorSaving={props.errorSaving}
                        // This is to avoid empty quotes from being used, mostly in storybook
                        buttonText={props.buttonText || undefined}
                        saveCompleteTimeout={1000}
                        resetOnComplete={props.resetOnSaveComplete}
                    />
                </ButtonGroup>
            </div>
        </section>
    );
}
